/**
 * OEE da Rotina TPL — Disciplina vs Conformidade (com expurgos).
 *
 * Disciplina: (O + Δ + Parado) / planejadas — apenas X penaliza.
 * Conformidade: O / (O + Δ) — Parado e X expurgados do denominador.
 */

import type { LstExecucao, LstStatus, LstTemplate } from '../types';
import { isTemplateDueOnDate } from './lstSchedule';
import { countsForConformityDenominator, isLstDisciplineExecuted } from '../utils/kamishibaiStatus';

export interface TplOeeCounts {
  planned: number;
  conforme: number;
  desvio: number;
  naoRealizado: number;
  processoParado: number;
  /** O + Δ + Parado — disciplina positiva */
  executadas: number;
}

export interface TplOeeMetrics {
  counts: TplOeeCounts;
  /** Disciplina do TPL (Adesão ao Plano) % */
  disciplinaPct: number;
  /** Conformidade do Processo % — O / (O + Δ) */
  conformidadePct: number;
}

function bucketStatus(status: LstStatus | null): Omit<TplOeeCounts, 'planned' | 'executadas'> {
  if (status === 'realizado') {
    return { conforme: 1, desvio: 0, naoRealizado: 0, processoParado: 0 };
  }
  if (status === 'realizado_com_desvio') {
    return { conforme: 0, desvio: 1, naoRealizado: 0, processoParado: 0 };
  }
  if (status === 'processo_parado') {
    return { conforme: 0, desvio: 0, naoRealizado: 0, processoParado: 1 };
  }
  return { conforme: 0, desvio: 0, naoRealizado: 1, processoParado: 0 };
}

export function computeTplOeeMetrics(
  templates: LstTemplate[],
  execucoes: LstExecucao[],
  dates: string[],
  monthDates?: string[],
): TplOeeMetrics {
  const monthRef = monthDates ?? dates;
  const counts: TplOeeCounts = {
    planned: 0,
    conforme: 0,
    desvio: 0,
    naoRealizado: 0,
    processoParado: 0,
    executadas: 0,
  };

  for (const date of dates) {
    const due = templates.filter((t) => t.active && isTemplateDueOnDate(t, date, monthRef));
    for (const tpl of due) {
      counts.planned += 1;
      const ex = execucoes.find((e) => e.template_id === tpl.id && e.data === date);
      const status = ex?.status ?? null;
      const bucket = bucketStatus(status);
      counts.conforme += bucket.conforme;
      counts.desvio += bucket.desvio;
      counts.naoRealizado += bucket.naoRealizado;
      counts.processoParado += bucket.processoParado;
      if (isLstDisciplineExecuted(status)) counts.executadas += 1;
    }
  }

  const disciplinaPct =
    counts.planned > 0 ? Math.round((counts.executadas / counts.planned) * 100) : 0;

  const conformidadeDenom = counts.conforme + counts.desvio;
  const conformidadePct =
    conformidadeDenom > 0 ? Math.round((counts.conforme / conformidadeDenom) * 100) : 0;

  return { counts, disciplinaPct, conformidadePct };
}

/** Métricas do turno atual (rotinas do dia). */
export function computeDayOeeMetrics(rotinas: Array<{ status: LstStatus }> = []): TplOeeMetrics {
  const items = rotinas ?? [];
  const counts: TplOeeCounts = {
    planned: items.length,
    conforme: 0,
    desvio: 0,
    naoRealizado: 0,
    processoParado: 0,
    executadas: 0,
  };

  for (const r of items) {
    const bucket = bucketStatus(r.status);
    counts.conforme += bucket.conforme;
    counts.desvio += bucket.desvio;
    counts.naoRealizado += bucket.naoRealizado;
    counts.processoParado += bucket.processoParado;
    if (isLstDisciplineExecuted(r.status)) counts.executadas += 1;
  }

  const disciplinaPct =
    counts.planned > 0 ? Math.round((counts.executadas / counts.planned) * 100) : 0;
  const conformidadeDenom = counts.conforme + counts.desvio;
  const conformidadePct =
    conformidadeDenom > 0 ? Math.round((counts.conforme / conformidadeDenom) * 100) : 0;

  return { counts, disciplinaPct, conformidadePct };
}

export function countsForConformityFromStatus(status: LstStatus | null): boolean {
  return countsForConformityDenominator(status);
}
